import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  IconButton,
  Box,
  Tooltip,
  Chip,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Shuffle as ShuffleIcon,
} from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 800,
    margin: '0 auto',
    padding: theme.spacing(2),
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  headerIcon: {
    width: 40,
    height: 40,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  headerTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  headerDescription: {
    color: theme.palette.text.secondary,
    maxWidth: 600,
    margin: '0 auto',
    marginBottom: theme.spacing(2),
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  statCard: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    minWidth: 140,
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  distributionCard: {
    position: 'relative',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[2],
    },
  },
  cardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  connectionInfo: {
    flex: 1,
  },
  connectionName: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sliderContainer: {
    flex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  slider: {
    flex: 1,
    '& .MuiSlider-markLabel': {
      fontSize: '0.75rem',
    },
  },
  percentageControl: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
    minWidth: 120,
    justifyContent: 'center',
  },
  percentageButton: {
    minWidth: 24,
    width: 24,
    height: 24,
    padding: 0,
  },
  percentageValue: {
    width: 40,
    textAlign: 'center',
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  removeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    width: 28,
    height: 28,
    opacity: 0,
    transition: 'opacity 0.2s ease',
    '$distributionCard:hover &': {
      opacity: 1,
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    borderStyle: 'dashed',
  },
  validationAlert: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  connectionSelect: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.common.white,
    },
  },
}));

const DistributionConfigurator = ({
  distributionRules,
  whatsapps,
  users,
  randomizationType,
  onUpdateRule,
  onAddRule,
  onRemoveRule,
  campaignEditable,
  totalPercentage,
}) => {
  const classes = useStyles();
  const theme = useTheme();


  // Função para auto-balancear as porcentagens
  const autoBalancePercentages = () => {
    const validRules = distributionRules.filter(rule => 
      randomizationType === "connection" ? rule.whatsappId : rule.userId
    );
    if (validRules.length === 0) return;

    // Calcula a porcentagem base e o resto
    const basePercentage = Math.floor(100 / validRules.length);
    const remainder = 100 % validRules.length;

    distributionRules.forEach((rule, index) => {
      if (randomizationType === "connection" ? rule.whatsappId : rule.userId) {
        let percentage = basePercentage;
        // Distribui o resto um por um para as primeiras regras
        if (index < remainder) percentage += 1;
        onUpdateRule(index, "percentage", percentage);
      }
    });
  };


  // Função para validar se pode criar campanha
  const canCreateCampaign = () => {
    // Se não há regras de distribuição, permite criar
    if (distributionRules.length === 0) return true;

    // Se há regras, deve ter ao menos um destino e total = 100%
    const hasValidRules = distributionRules.some(rule => {
      const hasDestination = randomizationType === "connection" 
        ? rule.whatsappId 
        : rule.userId;
      return hasDestination && rule.percentage > 0;
    });
    return hasValidRules && totalPercentage === 100;
  };

  return (
    <div className={classes.root}>
      {/* Cabeçalho Centralizado */}
      <div className={classes.header}>
        <ShuffleIcon className={classes.headerIcon} />
        <Typography variant="h5" className={classes.headerTitle}>
          Distribuição por {randomizationType === "connection" ? "Conexão" : "Usuário"}
        </Typography>
        <Typography variant="body2" className={classes.headerDescription}>
          Configure a distribuição dos contatos entre {randomizationType === "connection" ? "as conexões" : "os usuários"}
        </Typography>
      </div>

      {/* Cards de Estatísticas */}
      <div className={classes.statsContainer}>
        <Paper className={classes.statCard}>
          <Typography className={classes.statValue}>{totalPercentage}%</Typography>
          <Typography className={classes.statLabel}>Distribuído</Typography>
        </Paper>
        <Paper className={classes.statCard}>
          <Typography className={classes.statValue}>
            {distributionRules.filter(r => randomizationType === "connection" ? r.whatsappId : r.userId).length}
          </Typography>
          <Typography className={classes.statLabel}>{randomizationType === "connection" ? "Conexões" : "Usuários"}</Typography>
        </Paper>
        <Button
          variant="outlined"
          onClick={autoBalancePercentages}
          disabled={!campaignEditable || distributionRules.filter(r => randomizationType === "connection" ? r.whatsappId : r.userId).length === 0}
          startIcon={<ShuffleIcon />}
          size="small"
        >
          Auto-balancear
        </Button>
      </div>

      {/* Lista de Destinos */}
      {distributionRules.map((rule, index) => {
        const selectedDestination = randomizationType === "connection"
          ? whatsapps.find(w => w.id === rule.whatsappId)
          : users.find(u => u.id === rule.userId);

        return (
          <Paper key={index} className={classes.distributionCard}>
            <div className={classes.cardContent}>
              {/* Informações do Destino */}
              <div className={classes.connectionInfo}>
                {selectedDestination ? (
                  <Typography className={classes.connectionName}>
                    <ShuffleIcon fontSize="small" />
                    {selectedDestination.name}
                  </Typography>
                ) : (
                  <FormControl variant="outlined" size="small" className={classes.connectionSelect}>
                    <Select
                      value=""
                      onChange={(e) => onUpdateRule(index, randomizationType === "connection" ? "whatsappId" : "userId", e.target.value)}
                      disabled={!campaignEditable}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Selecionar {randomizationType === "connection" ? "conexão" : "usuário"}...</em>
                      </MenuItem>
                      {(randomizationType === "connection" ? whatsapps : users)
                        .filter(item => !distributionRules.some(r => 
                          randomizationType === "connection" 
                            ? r.whatsappId === item.id 
                            : r.userId === item.id
                        ))
                        .map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                )}
              </div>

              {/* Controles de Porcentagem */}
              {(randomizationType === "connection" ? rule.whatsappId : rule.userId) && (
                <div className={classes.sliderContainer}>
                  <Slider
                    className={classes.slider}
                    value={rule.percentage}
                    onChange={(e, value) => {
                      // Permite qualquer valor inteiro entre 0 e 100
                      const roundedValue = Math.round(value);
                      const limitedValue = Math.min(Math.max(roundedValue, 0), 100);
                      onUpdateRule(index, "percentage", limitedValue);
                    }}
                    disabled={!campaignEditable}
                    min={0}
                    max={100}
                    step={1}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 25, label: '25%' },
                      { value: 50, label: '50%' },
                      { value: 75, label: '75%' },
                      { value: 100, label: '100%' },
                    ]}
                    size="small"
                  />
                  
                  <div className={classes.percentageControl}>
                    <IconButton
                      className={classes.percentageButton}
                      onClick={() => {
                        // Se estiver em uma dezena exata (10, 20, 30...), subtrai 10
                        // Se não, vai para a dezena anterior (23 -> 20, 16 -> 10)
                        let newValue;
                        if (rule.percentage % 10 === 0) {
                          newValue = rule.percentage - 10;
                        } else {
                          newValue = Math.floor(rule.percentage / 10) * 10;
                        }
                        onUpdateRule(index, "percentage", Math.max(0, newValue));
                      }}
                      disabled={!campaignEditable || rule.percentage <= 0}
                      size="small"
                    >
                      -
                    </IconButton>
                    <Typography className={classes.percentageValue}>
                      {rule.percentage}%
                    </Typography>
                    <IconButton
                      className={classes.percentageButton}
                      onClick={() => {
                        // Se estiver em uma dezena exata (10, 20, 30...), soma 10
                        // Se não, vai para a próxima dezena (23 -> 30, 16 -> 20)
                        let newValue;
                        if (rule.percentage % 10 === 0) {
                          newValue = rule.percentage + 10;
                        } else {
                          newValue = Math.ceil(rule.percentage / 10) * 10;
                        }
                        onUpdateRule(index, "percentage", Math.min(100, newValue));
                      }}
                      disabled={!campaignEditable || rule.percentage >= 100}
                      size="small"
                    >
                      +
                    </IconButton>
                  </div>
                </div>
              )}
            </div>

            {/* Botão de Remover */}
            {(randomizationType === "connection" ? rule.whatsappId : rule.userId) && (
              <IconButton
                className={classes.removeButton}
                onClick={() => onRemoveRule(index)}
                disabled={!campaignEditable}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        );
      })}

      {/* Botão Adicionar */}
      <Button
        variant="outlined"
        onClick={onAddRule}
        disabled={!campaignEditable}
        startIcon={<AddIcon />}
        fullWidth
        className={classes.addButton}
      >
        Adicionar {randomizationType === "connection" ? "Conexão" : "Usuário"}
      </Button>

      {/* Alerta de Validação */}
      {totalPercentage !== 100 && distributionRules.some(r => randomizationType === "connection" ? r.whatsappId : r.userId) && (
        <Alert 
          severity="warning"
          className={classes.validationAlert}
          icon={<ShuffleIcon />}
        >
          <AlertTitle>Ajuste as porcentagens</AlertTitle>
          A soma total deve ser exatamente 100% para criar a campanha
        </Alert>
      )}
    </div>
  );
};

export default DistributionConfigurator;
